import { useRef, useState, useEffect } from "react"
import jsPDF from "jspdf"
import html2canvas from "html2canvas"
import { AutoComplete, Button, Popconfirm, Spin, Tag } from "antd"
import { useSubmissionsByForm } from "../logic/useEvaluationForm"
import useSearchUsersByName from "../logic/useSearchUsersByName"
import useAuth from "../../../../../../../core/hooks/useAuth"
import "./CollectEvaluationResponses.scss"
import {
  useEvaluationSkillRequests,
  useApproveSkillRequest,
  useRejectSkillRequest,
} from "../logic/useEvaluationSkillRequest"
import { Table } from "antd"

const CollectEvaluationResponses = ({ formId, onClose }) => {
  const [searchText, setSearchText] = useState("")
  const [selectedUser, setSelectedUser] = useState(null)
  const { authenticatedUser } = useAuth()
  const { data: users = [], isLoading: searching } = useSearchUsersByName(searchText)
  const { data: submissions = [], isLoading } = useSubmissionsByForm(formId)
  const submissionRefs = useRef({})

  const filteredSubmissions = submissions.filter((s) => s.user?.department === authenticatedUser?.department)

  const derivedUsers = searchText
    ? users.filter((u) => u.department === authenticatedUser?.department)
    : Array.from(new Map(filteredSubmissions.map((s) => [s.user.id, s.user])).values())

  const selectedSubmission =
    selectedUser && selectedUser.department === authenticatedUser?.department
      ? filteredSubmissions.find((s) => String(s.user.id) === String(selectedUser.id))
      : null

  const options = derivedUsers.map((u) => ({
    label: u.fullName || `${u.firstName || ""} ${u.lastName || ""}`.trim(),
    value: u.id,
    fullUser: u,
  }))

  const [currentIndex, setCurrentIndex] = useState(0)

  const sortedSubmissions = [...filteredSubmissions].sort((a, b) => new Date(b.submittedAt) - new Date(a.submittedAt))

  const { mutate: approve } = useApproveSkillRequest()
  const { mutate: reject } = useRejectSkillRequest()

  const handleApprove = (id) => {
    approve({ id, validatedBy: authenticatedUser?.id })
  }

  const handleReject = (id) => {
    reject({ id, validatedBy: authenticatedUser?.id })
  }

  useEffect(() => {
    if (!selectedUser && sortedSubmissions.length > 0) {
      setCurrentIndex(0)
    }
  }, [sortedSubmissions.length, selectedUser])

  useEffect(() => {
    if (selectedUser) {
      const foundIndex = sortedSubmissions.findIndex((s) => String(s.user.id) === String(selectedUser.id))
      if (foundIndex !== -1) {
        setCurrentIndex(foundIndex)
      }
    }
  }, [selectedUser, sortedSubmissions])

  const submissionToShow =
    selectedSubmission || (sortedSubmissions.length > currentIndex ? sortedSubmissions[currentIndex] : null)

  const { data: skillRequests = [] } = useEvaluationSkillRequests(submissionToShow?.id, submissionToShow?.user?.id)

  const handleClearSearch = () => {
    setSearchText("")
    setSelectedUser(null)
  }

  const downloadAsPDF = async (submissionId, userName) => {
    const target = submissionRefs.current[submissionId]
    if (!target) return

    try {
      const loadingOverlay = document.createElement("div")
      loadingOverlay.className = "pdf-loading-overlay"
      loadingOverlay.innerHTML = `
        <div class="pdf-loading-content">
          <div class="pdf-spinner"></div>
          <p>Generating PDF...</p>
        </div>
      `
      document.body.appendChild(loadingOverlay)

      const canvas = await html2canvas(target, {
        scale: 2,
        useCORS: true,
        logging: false,
      })

      const imgData = canvas.toDataURL("image/png")
      const pdf = new jsPDF("p", "mm", "a4")
      const pageWidth = pdf.internal.pageSize.getWidth()
      const imgProps = pdf.getImageProperties(imgData)
      const pdfHeight = (imgProps.height * pageWidth) / imgProps.width

      pdf.setFontSize(16)
      pdf.setTextColor(40, 40, 40)
      pdf.text("Evaluation Submission", 14, 15)

      pdf.setFontSize(10)
      pdf.setTextColor(100, 100, 100)
      pdf.text(`User: ${userName}`, 14, 25)
      pdf.text(`Date: ${new Date().toLocaleDateString()}`, 14, 30)

      pdf.addImage(imgData, "PNG", 0, 35, pageWidth, pdfHeight * 0.9)
      pdf.save(`${userName.replace(/\s+/g, "_")}_evaluation.pdf`)
      document.body.removeChild(loadingOverlay)
    } catch (error) {
      console.error("Error generating PDF:", error)
      const overlay = document.querySelector(".pdf-loading-overlay")
      if (overlay) document.body.removeChild(overlay)
    }
  }

  const skillRequestColumns = [
    {
      title: "Skill",
      dataIndex: ["skill", "name"],
      key: "skill",
      render: (name) => <span className="skill-name">{name || "-"}</span>,
    },
    {
      title: "Level",
      dataIndex: "level",
      key: "level",
      render: (level) => {
        const levelLabels = {
          1: "Beginner",
          2: "Intermediate",
          3: "Advanced",
          4: "Professional",
        }
        const getLevelColor = (level) => {
          const levelColors = {
            1: "success",
            2: "processing",
            3: "warning",
            4: "error",
          }
          return levelColors[level] || "default"
        }
        return (
          <Tag color={getLevelColor(level)} className="level-tag">
            {levelLabels[level] || "Unknown"}
          </Tag>
        )
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const getStatusColor = (status) => {
          const statusColors = {
            Approved: "success",
            Rejected: "error",
            Pending: "processing",
          }
          return statusColors[status] || "default"
        }
        return (
          <Tag color={getStatusColor(status)} className="status-tag">
            {status}
          </Tag>
        )
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (record) => (
        <div className="action-buttons-wrapper">
          <Popconfirm
            title="Are you sure you want to approve this skill?"
            onConfirm={() => handleApprove(record.id)}
            okText="Yes"
            cancelText="No"
            disabled={record.status === "Approved"}
          >
            <Button
              type="primary"
              size="small"
              disabled={record.status === "Approved"}
              className="action-btn validate-btn"
            >
              Validate
            </Button>
          </Popconfirm>

          <Popconfirm
            title="Are you sure you want to reject this skill?"
            onConfirm={() => handleReject(record.id)}
            okText="Yes"
            cancelText="No"
            disabled={record.status !== "Pending"}
          >
            <Button danger size="small" disabled={record.status !== "Pending"} className="action-btn reject-btn">
              Reject
            </Button>
          </Popconfirm>
        </div>
      ),
    },
  ]

  return (
    <div className="evaluation-summary-container">
      <div className="summary-header">
        <div className="header-top">
          <h2>Response Overview</h2>
          <button onClick={onClose} className="close-btn">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className="summary-stats">
          <div className="stat-box">
            <div className="stat-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" />
              </svg>
            </div>
            <div className="stat-content">
              <span className="label">Total Responses</span>
              <span className="value">{filteredSubmissions.length}</span>
            </div>
          </div>
        </div>

        <div className="search-section">
          <div className="search-container">
            <AutoComplete
              value={searchText}
              options={options}
              onSearch={setSearchText}
              onSelect={(userId, option) => {
                setSelectedUser(option.fullUser)
                setSearchText(option.label)
              }}
              onClear={handleClearSearch}
              allowClear
              placeholder="Search user by name..."
              className="search-input"
              notFoundContent={searching ? <Spin size="small" /> : "No users found"}
              filterOption={false}
            />
          </div>
        </div>
      </div>

      <div className="content-area">
        {isLoading ? (
          <div className="loading-state">
            <Spin size="large" />
            <p>Loading submissions...</p>
          </div>
        ) : selectedUser ? (
          selectedSubmission ? (
            <div className="submission-detail">
              <div className="submission-actions">
                <button
                  className="download-btn"
                  onClick={() => downloadAsPDF(selectedSubmission.user.id, selectedSubmission.user.fullName)}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4m4-5l5 5 5-5m-5 5V3"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                  </svg>
                  Download PDF
                </button>
              </div>

              <div className="submission-card" ref={(el) => (submissionRefs.current[selectedSubmission.user.id] = el)}>
                <div className="submission-header">
                  <div className="user-info">
                    <div className="user-avatar">{selectedSubmission.user.fullName.charAt(0).toUpperCase()}</div>
                    <div className="user-details">
                      <h3>{selectedSubmission.user.fullName}</h3>
                    </div>
                  </div>
                  <div className="submission-meta">
                    <span className="timestamp">
                      Submitted: {new Date(selectedSubmission.submittedAt).toLocaleString()}
                    </span>
                  </div>
                </div>

                <div className="responses-container">
                  {selectedSubmission.responses.map((r, idx) => (
                    <div key={idx} className="response-item">
                      <div className="response-header">
                        <span className="section-badge">{r.question.section}</span>
                        <span className="question-number">Q{idx + 1}</span>
                      </div>
                      <div className="question-text">{r.question.text}</div>
                      <div className="answer-box">
                        <div className="answer-content">{r.value || "No response provided"}</div>
                      </div>
                    </div>
                  ))}

                  {skillRequests.length > 0 && (
                    <div className="skill-requests-section">
                      <h4>Skill Requests</h4>
                      <div className="skill-requests-table-container">
                        <Table
                          columns={skillRequestColumns}
                          dataSource={skillRequests}
                          rowKey="id"
                          pagination={false}
                          size="middle"
                          className="modern-skill-table"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="empty-state">
              <div className="empty-icon">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="1" />
                </svg>
              </div>
              <h3>No Submission Found</h3>
              <p>This user hasn't submitted their evaluation yet.</p>
            </div>
          )
        ) : submissionToShow ? (
          <div className="submission-detail">
            <div className="submission-actions">
              <button
                className="download-btn"
                onClick={() => downloadAsPDF(submissionToShow.user.id, submissionToShow.user.fullName)}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4m4-5l5 5 5-5m-5 5V3"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                </svg>
                Download PDF
              </button>
            </div>

            {!selectedUser && (
              <div className="navigation-arrows">
                <button
                  disabled={currentIndex === 0}
                  onClick={() => setCurrentIndex((i) => Math.max(i - 1, 0))}
                  className="nav-btn"
                >
                  ← Previous
                </button>
                <span>
                  {currentIndex + 1} / {sortedSubmissions.length}
                </span>
                <button
                  disabled={currentIndex === sortedSubmissions.length - 1}
                  onClick={() => setCurrentIndex((i) => Math.min(i + 1, sortedSubmissions.length - 1))}
                  className="nav-btn"
                >
                  Next →
                </button>
              </div>
            )}

            <div className="submission-card" ref={(el) => (submissionRefs.current[submissionToShow.user.id] = el)}>
              <div className="submission-header">
                <div className="user-info">
                  <div className="user-avatar">{submissionToShow.user.fullName.charAt(0).toUpperCase()}</div>
                  <div className="user-details">
                    <h3>{submissionToShow.user.fullName}</h3>
                  </div>
                </div>
                <div className="submission-meta">
                  <span className="timestamp">
                    Submitted: {new Date(submissionToShow.submittedAt).toLocaleString()}
                  </span>
                </div>
              </div>

              <div className="responses-container">
                {submissionToShow.responses.map((r, idx) => (
                  <div key={idx} className="response-item">
                    <div className="response-header">
                      <span className="section-badge">{r.question.section}</span>
                      <span className="question-number">Q{idx + 1}</span>
                    </div>
                    <div className="question-text">{r.question.text}</div>
                    <div className="answer-box">
                      <div className="answer-content">{r.value || "No response provided"}</div>
                    </div>
                  </div>
                ))}

                {skillRequests.length > 0 && (
                  <div className="skill-requests-section">
                    <h4>Skill Requests</h4>
                    <div className="skill-requests-table-container">
                      <Table
                        columns={skillRequestColumns}
                        dataSource={skillRequests}
                        rowKey="id"
                        pagination={false}
                        size="middle"
                        className="modern-skill-table"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="empty-state">
            <div className="empty-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
                <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="1" />
                <path d="m21 21-4.35-4.35" stroke="currentColor" strokeWidth="1" />
              </svg>
            </div>
            <h3>No Submissions Available</h3>
            <p>No users from your department have submitted their evaluations yet.</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default CollectEvaluationResponses
